import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { logError } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import {
  EMPTY_USPACY_LEGAL_ACTIONS,
  type UspacyLegalActionsState,
} from '@/components/legal/UspacyLegalActions';
import { UspacyProcessingCard } from '@/components/settings/UspacyProcessingCard';
import { Loader2, Shield, ShieldCheck, ChevronDown, AlertCircle } from 'lucide-react';
import { cn, resolveLocale } from '@/lib/utils';
import { ConfirmDialog } from '@/components/modals';
import type {
  ParticipantConsentType,
  WithdrawableConsentType,
} from '@/lib/api/domains/consent.api';
import { participantConsentTypeSchema } from '@aica/api-types/schemas';
import type { UspacyProcessingCommand } from '@/lib/api/domains/organizations.api';
import { useRole } from '@/contexts/RoleContext';
import { consentStatusQueryKey } from '@/features/onboarding/useConsentStatus';
import { AccountAgreementsJournal } from './AccountAgreementsJournal';
import { ConsentEventRow } from './ConsentEventRow';
import { JournalNewerEventsNotice } from './JournalNewerEventsNotice';
import { JournalRefreshFailedNotice } from './JournalRefreshFailedNotice';
import { useConsentJournal } from './useConsentJournal';
import {
  normalizeUspacyPlacementLocale,
  uspacyLegalActivationSchema,
} from '@aica/api-types';

/**
 * Карточки статусов — только то, что интерфейс умеет подтвердить.
 *
 * Было четыре: `recording`, `analysis`, `storage`, `crm_delivery`. Последние
 * три не пишет ни один код-путь, поэтому они показывали «не зафиксировано» у
 * каждой организации навсегда. При разборе инцидента это прочли как причину
 * остановки анализа — ею оно не было (#401).
 */
const CONSENT_TYPES: ParticipantConsentType[] = [...participantConsentTypeSchema.options];

/**
 * Варианты фильтра журнала согласий участников — шире, чем карточки статуса,
 * и это не оплошность.
 *
 * Журнал показывает всё, что записано о согласиях участников, включая
 * исторические строки (`analysis`, `storage`, `crm_delivery`), — их видно в
 * «Все». Отдельным пунктом фильтра они не предлагаются: своего кода-пути у них
 * нет, и выбор такого пункта у любой организации давал бы пустой список (#401).
 *
 * Договора аккаунта здесь нет (#1100): принятие Условий и Политики и подписка
 * на рассылку — не согласие участника разговора, и рядом с «Отозвать» они
 * читались как отзываемое согласие. Они показываются отдельным блоком ниже.
 */
const JOURNAL_FILTER_TYPES: string[] = [...participantConsentTypeSchema.options];

interface UspacyLegalDraft {
  key: string;
  actions: UspacyLegalActionsState;
}

export function ConsentSection() {
  const { t } = useTranslation(['settings', 'common']);
  const { apiClient } = useApiClient();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { selectedOrgId, isOrgSwitching, globalRole, isOwner } = useRole();
  const [typeFilter, setTypeFilter] = useState<string>('all');
  // Отзывать можно и то, чего продукт больше не выдаёт: исторические
  // согласия в журнале есть, и право их отозвать никуда не делось.
  const [withdrawTarget, setWithdrawTarget] = useState<WithdrawableConsentType | null>(
    null
  );
  const [withdrawUspacyOpen, setWithdrawUspacyOpen] = useState(false);
  const [uspacyLegalDraft, setUspacyLegalDraft] = useState<UspacyLegalDraft>({
    key: '',
    actions: EMPTY_USPACY_LEGAL_ACTIONS,
  });
  const actionsDisabled = !selectedOrgId || isOrgSwitching;
  const uspacyLegalLocale = normalizeUspacyPlacementLocale(resolveLocale());
  // Platform-owner privilege is operational access, not evidence that an Aelo
  // operator represents the Customer for its processing instruction — which is
  // why the route answers 403 "Customer authority required" to `auth.isOwner`.
  // `globalRole` alone cannot express that: a platform owner is handed 'admin'
  // for EVERY organization (PermissionContext), so testing it by itself would
  // show an Aelo super-admin enabled controls whose every click ends in the
  // same failure toast. Mirror the server's rule instead of half of it.
  const canManageIntegration =
    !isOwner && (globalRole === 'client' || globalRole === 'admin');

  const statusQuery = useQuery({
    queryKey: ['consent-status', selectedOrgId],
    queryFn: () => apiClient.consent.getStatus(),
    enabled: !!selectedOrgId,
  });

  const legacyGranted = statusQuery.data?.legacyGranted ?? [];

  const journal = useConsentJournal(
    'participant',
    typeFilter === 'all' ? undefined : typeFilter
  );

  const uspacyQuery = useQuery({
    queryKey: ['uspacy-processing-state', selectedOrgId, uspacyLegalLocale],
    queryFn: () =>
      apiClient.organizations.getUspacyProcessingState(selectedOrgId!, uspacyLegalLocale),
    enabled: !!selectedOrgId,
  });
  const uspacyLegalDraftKey = `${selectedOrgId ?? 'none'}:${
    uspacyQuery.data?.processing?.revision ?? 'none'
  }`;
  // A draft belongs to one organization and one processing-state revision.
  // Switching orgs or observing an external lifecycle change therefore makes
  // every legal action unchecked without a state-setting effect.
  const uspacyLegalActions =
    uspacyLegalDraft.key === uspacyLegalDraftKey
      ? uspacyLegalDraft.actions
      : EMPTY_USPACY_LEGAL_ACTIONS;
  const allUspacyLegalActions = Object.values(uspacyLegalActions).every(Boolean);
  const setUspacyLegalAction = (
    action: keyof UspacyLegalActionsState,
    checked: boolean
  ) => {
    setUspacyLegalDraft((current) => ({
      key: uspacyLegalDraftKey,
      actions: {
        ...(current.key === uspacyLegalDraftKey
          ? current.actions
          : EMPTY_USPACY_LEGAL_ACTIONS),
        [action]: checked,
      },
    }));
  };

  const withdrawMutation = useMutation({
    mutationFn: (consentType: WithdrawableConsentType) => {
      if (actionsDisabled) throw new Error('Organization switch in progress');
      return apiClient.consent.withdraw(consentType);
    },
    onSuccess: (result) => {
      // Отзыв, который ничего не остановил, обязан сказать об этом.
      //
      // Гейт приёма читает подтверждение только при `consentRequired: true`.
      // Организация, заявившая, что её обработка не опирается на согласие
      // участников, получала бы одинаковый «Согласие отозвано» — и понимала бы
      // его как «обработка прекращена», которой не было.
      //
      // Совета «смените основание» здесь БЫЛО, и он вёл в никуда: экран
      // соответствия патчит только `consentMode`/`jurisdiction`/`legalBasis`
      // (`consentConfigUpdateSchema` в settings.ts именно этой тройкой и
      // ограничен), а гейт приёма читает `consentRequired` — поле, которого не
      // меняет ни один экран продукта. Человек выполнил бы инструкцию и не
      // получил обещанного. Названо то, что приём действительно
      // останавливает, — и названо ДЛЯ ОБОИХ источников: у организации на
      // публичном API кнопки «Отключить» нет вовсе (`ConnectionTab` её прячет:
      // приём там держится не подключением, а живыми ключами), и совет про
      // отключение источника был бы для неё таким же тупиком.
      if (result?.processingStopped === false) {
        toast.info(
          t('consent.withdrawnNoEffectTitle', 'Consent withdrawn'),
          t(
            'consent.withdrawnNoEffectBody',
            'Processing continues: your organization has declared that its legal basis does not require participant consent. Changing that basis does not stop intake either — it is a declaration, not a switch. To stop it, disconnect the communication source in Integration settings; if conversations arrive through the public API, revoke the keys in API Keys.'
          )
        );
      } else {
        toast.success(t('consent.withdrawn', 'Consent withdrawn'));
      }
      setWithdrawTarget(null);
      // Сбрасывать надо ВСЕ три кеша статуса, а не свой.
      //
      // Отзыв `recording` снимает подтверждение комплайенса, то есть меняет
      // состояние, на которое смотрит не только этот экран: гейт согласия и
      // баннер дашборда читают `consentStatusQueryKey`, чек-лист
      // админ-дашборда — свой ключ. Пока сбрасывался один, приём был уже
      // остановлен, а остальная часть приложения продолжала показывать
      // «комплайенс подтверждён» до следующей перезагрузки.
      for (const queryKey of [
        ['consent-status', selectedOrgId],
        consentStatusQueryKey(selectedOrgId),
        ['admin-consent-status', selectedOrgId],
        ['consent-events', selectedOrgId],
      ]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
    onError: () => {
      logError('consent.withdraw_failed', { component: 'ConsentSection' });
      toast.error(t('consent.withdrawFailed', 'Failed to withdraw consent'));
    },
  });

  const integrationMutation = useMutation({
    mutationFn: (command: UspacyProcessingCommand) => {
      if (actionsDisabled || !selectedOrgId || !canManageIntegration) {
        throw new Error('Organization switch in progress or access denied');
      }
      return apiClient.organizations.setUspacyProcessingState(selectedOrgId, command);
    },
    onSuccess: (result) => {
      setUspacyLegalDraft({
        key: uspacyLegalDraftKey,
        actions: EMPTY_USPACY_LEGAL_ACTIONS,
      });
      if (result.state === 'active') {
        if (result.subscriptionActive === false) {
          toast.error(
            t(
              'consent.uspacy.subscriptionIncomplete',
              'Authorization was saved, but the Uspacy event subscription is incomplete'
            )
          );
        } else {
          toast.success(
            t('consent.uspacy.reauthorizedSuccess', 'Uspacy processing reauthorized')
          );
        }
      } else {
        toast.success(
          result.state === 'paused'
            ? t('consent.uspacy.pausedSuccess', 'Uspacy processing paused')
            : t('consent.uspacy.withdrawnSuccess', 'Uspacy processing withdrawn')
        );
      }
      void queryClient.invalidateQueries({
        queryKey: ['uspacy-processing-state', selectedOrgId],
      });
      // The CRM stage references (the names of every CRM, `/crm/stage-names`,
      // included) leave out a Uspacy whose processing is paused. A refetch is
      // not enough: with Uspacy the only CRM it answers 409, a failed refetch
      // keeps the last good list, and the paused CRM's stages would stay named
      // on screen. `reset` drops the cached lists and refetches the mounted ones.
      void queryClient.resetQueries({
        queryKey: ['crm-stages-reference', selectedOrgId],
      });
    },
    onError: () => {
      logError('uspacy.processing_state_failed', { component: 'ConsentSection' });
      toast.error(t('consent.uspacy.actionFailed', 'Failed to update Uspacy processing'));
    },
  });

  const reauthorizeUspacy = () => {
    const documents = uspacyQuery.data?.legalDocuments;
    if (!documents || !allUspacyLegalActions) return;

    const parsedLegal = uspacyLegalActivationSchema.safeParse({
      terms: {
        documentId: documents.terms.documentId,
        version: documents.terms.version,
        accepted: true,
      },
      privacy: {
        documentId: documents.privacy.documentId,
        version: documents.privacy.version,
        acknowledged: true,
      },
      processingInstruction: {
        documentId: documents.processingInstruction.documentId,
        version: documents.processingInstruction.version,
        confirmed: true,
        authorityConfirmed: true,
      },
      language: uspacyLegalLocale,
    });
    // The only way this fails is a server document newer than the bundled SPA:
    // the payload is built one statement above from the server's own documents,
    // and the schema pins each documentId/version as a literal. Say exactly
    // that — "failed to update" would send the operator looking for an outage.
    if (!parsedLegal.success) {
      toast.error(
        t(
          'consent.uspacy.documentsOutdated',
          'The legal documents were updated. Reload the page and confirm the current versions.'
        )
      );
      return;
    }
    integrationMutation.mutate({ state: 'active', legal: parsedLegal.data });
  };

  // Only the participant-consent queries can empty this section. The Uspacy
  // lifecycle query runs for EVERY organization, including the ones that never
  // installed it, so letting it into this branch traded an integration hiccup
  // for the whole consent journal — status, audit log and the ability to
  // withdraw a participant consent — on accounts that do not use Uspacy at all.
  // Its failure is reported in place instead, below.
  // A later journal page that failed does not count: what was shown stays,
  // with a note under it — the status cards and «Withdraw» must not vanish
  // over a page nobody needed to act.
  if (statusQuery.error || journal.loadFailed) {
    const err = statusQuery.error || journal.error;
    // The account-agreement block reads its own half of the journal and does
    // not depend on the participant data that failed here (#1100).
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center py-8 text-center">
          <AlertCircle className="h-8 w-8 text-danger mb-2" />
          <h3 className="text-sm font-medium text-fg mb-1">
            {t('common:common.errorLoadingData', 'Failed to load data')}
          </h3>
          <p className="text-xs text-fg-muted">
            {err instanceof Error ? err.message : t('common:common.errorUnknown')}
          </p>
        </div>
        <AccountAgreementsJournal />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-xs text-fg-muted">
        {t(
          'consent.journalNotice',
          'Consent journal: records consents granted and withdrawn by call participants. Processing of recordings is governed by the legal basis selected in Compliance settings.'
        )}
      </p>

      {uspacyQuery.error ? (
        <Card className="p-4" data-testid="uspacy-processing-unavailable">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 text-warning" />
            <p className="text-xs text-fg-muted">
              {t(
                'consent.uspacy.stateUnavailable',
                'Could not load the Uspacy integration status. Participant consent below is unaffected.'
              )}
            </p>
          </div>
        </Card>
      ) : null}

      {uspacyQuery.data?.installation ? (
        <UspacyProcessingCard
          installation={uspacyQuery.data.installation}
          processing={uspacyQuery.data.processing}
          legalDocuments={uspacyQuery.data.legalDocuments}
          canManage={canManageIntegration}
          actionsDisabled={actionsDisabled}
          mutationPending={integrationMutation.isPending}
          legalActions={uspacyLegalActions}
          allLegalActions={allUspacyLegalActions}
          onLegalActionChange={setUspacyLegalAction}
          onPause={() => integrationMutation.mutate({ state: 'paused' })}
          onWithdraw={() => setWithdrawUspacyOpen(true)}
          onReauthorize={reauthorizeUspacy}
        />
      ) : null}

      {/* Status Cards.
          Сетка и скелетоны — по числу карточек, которое действительно
          рисуется. При `md:grid-cols-3` и трёх заглушках экран сперва показывал
          три плейсхолдера во всю ширину, а потом схлопывался в одну карточку в
          левой трети — две трети пустоты после загрузки. */}
      <div
        data-testid="consent-status-cards"
        className={cn(
          'grid gap-4',
          CONSENT_TYPES.length > 1 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1'
        )}
      >
        {statusQuery.isLoading
          ? Array.from({ length: CONSENT_TYPES.length }).map((_, i) => (
              <Card key={i} className="p-4 animate-pulse">
                <div className="h-4 bg-bg-muted rounded w-24 mb-2" />
                <div className="h-6 bg-bg-muted rounded w-16" />
              </Card>
            ))
          : CONSENT_TYPES.map((consentType) => {
              const granted =
                statusQuery.data?.[consentType as keyof typeof statusQuery.data] ?? false;
              // Настройки не прочитались — состояние НЕИЗВЕСТНО, а не «нет».
              //
              // Разница здесь стоит клиенту обработки: гейт приёма на той же
              // ошибке разбора продолжает работу (fail-open), а «не
              // зафиксировано» прячет кнопку «Отозвать» — единственное, чем
              // это останавливается. Отзыв к порче терпим, поэтому действие
              // остаётся доступным.
              const unreadable = statusQuery.data?.complianceSettingsUnreadable === true;
              return (
                <Card key={consentType} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-fg">
                      {t(`consent.types.${consentType}`, consentType.replace(/_/g, ' '))}
                    </span>
                    {unreadable ? (
                      <AlertCircle className="h-4 w-4 text-warning" />
                    ) : granted ? (
                      <ShieldCheck className="h-4 w-4 text-success" />
                    ) : (
                      <Shield className="h-4 w-4 text-fg-muted" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        'text-xs font-medium px-2 py-0.5 rounded',
                        unreadable
                          ? 'bg-warning-dim text-warning-fg'
                          : granted
                            ? 'bg-success-dim text-success-fg'
                            : 'bg-bg-muted text-fg-muted'
                      )}
                    >
                      {unreadable
                        ? t('consent.statusUnknown', 'Unknown')
                        : granted
                          ? t('consent.granted', 'Granted')
                          : t('consent.notRecorded', 'Not Recorded')}
                    </span>
                    {(granted || unreadable) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setWithdrawTarget(consentType)}
                        disabled={actionsDisabled || withdrawMutation.isPending}
                        className="text-xs text-danger hover:text-danger"
                      >
                        {t('consent.withdraw', 'Withdraw')}
                      </Button>
                    )}
                  </div>
                  {unreadable ? (
                    <p className="mt-2 text-xs text-fg-muted">
                      {t(
                        'consent.settingsUnreadableNote',
                        'Compliance settings cannot be read, so the confirmation state is unknown. Processing continues meanwhile. Withdrawing clears the confirmation and restores a defined state.'
                      )}
                    </p>
                  ) : null}
                </Card>
              );
            })}
      </div>

      {/* Исторические согласия.
          Появляются только у организации, у которой такое согласие ДЕЙСТВУЕТ —
          то есть почти никогда. Карточек по этим типам в статусе нет намеренно
          (их не пишет ни один код-путь, и вечное «не зафиксировано» у всех
          подряд и было поводом для #401), но вместе с карточкой у тех, у кого
          согласие записано, исчезла и единственная кнопка «Отозвать». Право
          без способа им воспользоваться — это не сужение экрана, а отказ в
          отзыве, который обещан Art. 7(3). */}
      {legacyGranted.length > 0 ? (
        <div data-testid="consent-legacy-grants" className="space-y-2">
          <h3 className="text-sm font-medium text-fg">
            {t('consent.legacyTitle', 'Historical consents')}
          </h3>
          <p className="text-xs text-fg-muted">
            {t(
              'consent.legacyNotice',
              'These consent types are no longer issued by the product. The records remain in force and can be withdrawn.'
            )}
          </p>
          {legacyGranted.map((consentType) => (
            <Card
              key={consentType}
              className="flex items-center justify-between gap-3 p-3"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 shrink-0 text-success" />
                <span className="text-sm text-fg">
                  {t(`consent.types.${consentType}`, consentType.replace(/_/g, ' '))}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setWithdrawTarget(consentType)}
                disabled={actionsDisabled || withdrawMutation.isPending}
                className="text-xs text-danger hover:text-danger"
              >
                {t('consent.withdraw', 'Withdraw')}
              </Button>
            </Card>
          ))}
        </div>
      ) : null}

      {/* Audit Log */}
      <div data-testid="participant-consent-journal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-fg">
            {t('consent.auditLog', 'Audit Log')}
          </h3>
          <Select
            value={typeFilter}
            onValueChange={(val) => {
              setTypeFilter(val as string);
            }}
            options={[
              { value: 'all', label: t('common:common.all', 'All') },
              ...JOURNAL_FILTER_TYPES.map((ct) => ({
                value: ct,
                label: t(`consent.types.${ct}`, ct.replace(/_/g, ' ')),
              })),
            ]}
            className="w-40"
          />
        </div>

        {journal.isPending ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-fg-muted" />
          </div>
        ) : journal.events.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-center">
            <Shield className="h-8 w-8 text-fg-muted mb-2" />
            <p className="text-sm text-fg-muted">
              {t('consent.noEvents', 'No consent events recorded')}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {journal.newerAvailable && (
              <JournalNewerEventsNotice onRefresh={journal.refresh} />
            )}
            {journal.events.map((event) => (
              <ConsentEventRow key={event.id} event={event} />
            ))}

            {journal.loadMoreFailed && (
              <p className="text-xs text-danger" role="alert">
                {t('common:common.errorLoadingData', 'Failed to load data')}
              </p>
            )}

            {/* Load More */}
            {journal.hasMore && (
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={journal.loadMore}
                  disabled={journal.loadMoreBlocked}
                >
                  <ChevronDown className="h-4 w-4 mr-1" />
                  {t('common:common.loadMore', 'Load More')}
                </Button>
              </div>
            )}
          </div>
        )}
        {/* Outside the branches: «no consent events» is a claim too, and an
            empty journal that could not be re-read must not keep making it. */}
        {journal.refreshFailed && (
          <JournalRefreshFailedNotice onRetry={journal.refresh} />
        )}
      </div>

      <AccountAgreementsJournal />

      <ConfirmDialog
        isOpen={withdrawTarget !== null && !actionsDisabled}
        onClose={() => setWithdrawTarget(null)}
        onConfirm={() => {
          if (withdrawTarget && !actionsDisabled) {
            withdrawMutation.mutate(withdrawTarget);
          }
        }}
        title={t('consent.withdrawTitle', 'Withdraw Consent?')}
        message={t(
          'consent.withdrawConfirm',
          'Are you sure you want to withdraw this consent?'
        )}
        confirmText={t('consent.withdraw', 'Withdraw')}
        variant="warning"
      />

      <ConfirmDialog
        isOpen={withdrawUspacyOpen && !actionsDisabled}
        onClose={() => setWithdrawUspacyOpen(false)}
        onConfirm={() => {
          setWithdrawUspacyOpen(false);
          integrationMutation.mutate({ state: 'withdrawn' });
        }}
        title={t(
          'consent.uspacy.withdrawTitle',
          'Withdraw the Uspacy processing instruction?'
        )}
        message={t(
          'consent.uspacy.withdrawConfirm',
          'New Uspacy calls will be blocked immediately. Reactivation requires a new current instruction from an authorized representative.'
        )}
        confirmText={t('consent.uspacy.withdraw', 'Withdraw instruction')}
        variant="warning"
      />
    </div>
  );
}
